import type { Language } from '@/i18n/dictionary';

/**
 * The flag beside each language code, drawn rather than typed.
 *
 * **It used to be an emoji, and on the kiosk's own hardware it was two empty boxes.** `🇺🇸` is not a
 * character: it is U+1F1FA + U+1F1F8, the regional indicators for U and S, and a flag appears only where the
 * installed emoji font happens to carry a ligature for that pair. **Windows carries none** — Microsoft has
 * never shipped country flags in Segoe UI Emoji — and decision 0140 puts this app on *"a Chromebook or a
 * Surface"*. A Surface is Windows, so on half the sanctioned hardware the first control a candidate touches
 * rendered as tofu. Reported by the Operator on 2026-09-04, from his own browser.
 *
 * ⚠️ **This is a deliberate departure from the frame, and the frame has the same defect.** Figma's
 * `Language Selector` (11874:18814) is a TEXT node whose characters are those same emoji — it renders for
 * the designer because macOS does carry the ligature. Following the frame literally reproduces the bug, so
 * the drawing is the fix and the frame is owed a correction. Declared in `design-qa/known-departures.md`.
 *
 * Sized 18×12 to sit in the emoji's footprint at the frame's 14px text on a 16px line, so nothing moves
 * around it.
 */
export function FlagIcon({ code }: { readonly code: Language }) {
  // Per language, so that the two segments on screen together never collide on one id.
  const clipId = `flag-clip-${code}`;

  return (
    <svg aria-hidden="true" className="shrink-0" height="12" viewBox="0 0 18 12" width="18">
      <clipPath id={clipId}>
        <rect height="12" rx="1.5" width="18" />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>{code === 'en' ? <UnitedStates /> : <Spain />}</g>
      {/*
        A hairline, because both flags are part white and the selected segment sits on a white pill —
        without it the shape bleeds into its own background and reads as a smudge rather than a flag.
      */}
      <rect
        fill="none"
        height="11"
        rx="1.5"
        stroke="rgb(0 0 0 / 0.18)"
        width="17"
        x="0.5"
        y="0.5"
      />
    </svg>
  );
}

/**
 * Seven stripes, not thirteen — a rendering decision, not an oversight.
 *
 * Thirteen stripes across 12px is 0.92px each. Chromebooks are commonly 1× at 1366×768, where sub-pixel
 * stripes antialias into a flat pink rectangle: a flag that is *technically* right and reads as no flag at
 * all. Seven hold their edges at 1× and still read as this flag at a glance, which is the whole job of a
 * 16px icon. Every emoji font makes the same simplification at this size.
 *
 * The canton carries no stars for the same reason: fifty of them inside 6×7px is grey noise.
 */
function UnitedStates() {
  const STRIPE = 12 / 7;
  return (
    <>
      <rect fill="#B31942" height="12" width="18" />
      {[1, 3, 5].map((row) => (
        <rect fill="#FFFFFF" height={STRIPE} key={row} width="18" y={row * STRIPE} />
      ))}
      <rect fill="#0A3161" height={STRIPE * 4} width="7.2" />
    </>
  );
}

/** Three bands, 1:2:1 — the real proportion, and it survives any size. */
function Spain() {
  return (
    <>
      <rect fill="#AA151B" height="12" width="18" />
      <rect fill="#F1BF00" height="6" width="18" y="3" />
    </>
  );
}
